import React, { useState, useEffect } from 'react';
import { BuildingStorefrontIcon } from '@heroicons/react/24/solid';
import { logoAPI } from '../../services/logoAPI';

interface LogoDisplayProps {
  type: 'main' | 'header' | 'login' | 'favicon';
  className?: string;
  style?: React.CSSProperties;
  fallbackIcon?: React.ReactNode;
  alt?: string;
}

interface LogoData {
  id: number;
  name: string;
  logo_type: string;
  image_url: string;
  alt_text: string;
  width?: number;
  height?: number;
}

interface ActiveLogos {
  main: LogoData | null;
  header: LogoData | null;
  login: LogoData | null;
  favicon: LogoData | null;
}

const LogoDisplay: React.FC<LogoDisplayProps> = ({ 
  type, 
  className = '', 
  style = {}, 
  fallbackIcon, 
  alt 
}) => {
  const [logoState, setLogoState] = useState<{
    logo: LogoData | null;
    loading: boolean;
    error: boolean;
  }>({ logo: null, loading: true, error: false });
  const { logo, loading, error } = logoState;

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        setLogoState(prev => ({ ...prev, loading: true }));
        console.log(`🔍 LogoDisplay: Fetching logo for type: ${type}`);
        const response = await logoAPI.getActiveLogos();
        console.log('🔍 LogoDisplay: API response:', response.data);
        const activeLogos: ActiveLogos = response.data;
        
        if (activeLogos[type]) {
          console.log(`✅ LogoDisplay: Found logo for ${type}:`, activeLogos[type]);
        } else {
          console.log(`⚠️ LogoDisplay: No logo found for type: ${type}`);
        }
        setLogoState({ logo: activeLogos[type] || null, loading: false, error: false });
      } catch (err) {
        console.error('❌ LogoDisplay: Error fetching logo:', err);
        setLogoState(prev => ({ ...prev, error: true, loading: false }));
      }
    };

    fetchLogo();
  }, [type]);

  if (loading) {
    return <div className={`logo-loading ${className}`} style={style}>...</div>;
  }

  if (error || !logo || !logo.image_url) {
    return (
      <div className={`logo-fallback ${className}`} style={style}>
        {fallbackIcon || <BuildingStorefrontIcon style={{ width: '24px', height: '24px' }} />}
      </div>
    );
  }

  return (
    <img
      src={logo.image_url}
      alt={alt || logo.alt_text || logo.name}
      className={`logo-image ${className}`}
      style={{
        width: logo.width ? `${logo.width}px` : (style.width || 'auto'),
        height: logo.height ? `${logo.height}px` : (style.height || 'auto'),
        objectFit: 'cover',
        ...style
      }}
      onError={() => setLogoState(prev => ({ ...prev, error: true }))}
    />
  );
};

export default LogoDisplay;